/**
 * Notifications — a derived feed of things others did to the caller (web port).
 *
 * Web twin of apps/mobile/app/notifications.tsx. GET /me/notifications is a
 * READ-ONLY view over audit_log: curated actions where SOMEONE ELSE acted on
 * the caller (cooperative invite/removal, KYC review, join-request decisions).
 * The server keeps no read/unread state — there is no last-seen badge on web
 * for now.
 *
 * Each row renders a humanised relative time from `occurredAt` plus a title:
 * for the marketplace kinds (offer / order / demand) we render a fully-localised
 * client string (the server label may be terse / English-only); every other
 * kind keeps the server-derived `title` verbatim, so a kind the client doesn't
 * know about still renders exactly what the server sent. The per-kind icon is a
 * visual cue only.
 *
 * Rows are bucketed by recency into Today / Yesterday / Earlier sections (the
 * feed is server-sorted newest-first, so each bucket stays contiguous and in
 * order). Empty buckets render no header.
 *
 * Paging mirrors _app.my-activity.tsx: start at the 30 most-recent rows and
 * APPEND the next page on "Load more" (accumulated in component state, keyed by
 * offset to dedupe a page that arrives twice). The button hides once we've
 * loaded >= total.
 *
 * Mode-aware (TD-094): all colours come from MUI theme-path strings in `sx`
 * (text.primary / text.secondary / divider) — no hardcoded brand values.
 *
 * Contract: MyNotificationsPageDto { data: MyNotificationDto[], pagination:
 * { total, limit, offset } }; MyNotificationDto { id, kind, occurredAt, title }.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ListItem,
  Stack,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { meControllerGetNotifications } from '../api/generated/me/me';
import type { MyNotificationDto, MyNotificationsPageDto } from '../api/generated/models';
import { getReadIds, markManyRead, markRead } from '../notifications/readState';

const PAGE_SIZE = 30;

/**
 * Humanise an ISO date-time into a coarse relative label ("just now",
 * "5 minutes ago", "3 hours ago", "2 days ago") and fall back to an absolute
 * date once older than a week. Coarse buckets read naturally on a feed and
 * don't jitter on re-render. i18next pluralises via the `count` param.
 */
function formatRelativeTime(iso: string, now: number, t: TFunction): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  const deltaSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (deltaSec < 60) return t('notifications.time.justNow');
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return t('notifications.time.minutesAgo', { count: deltaMin });
  const deltaHrs = Math.floor(deltaMin / 60);
  if (deltaHrs < 24) return t('notifications.time.hoursAgo', { count: deltaHrs });
  const deltaDays = Math.floor(deltaHrs / 24);
  if (deltaDays < 7) return t('notifications.time.daysAgo', { count: deltaDays });
  return new Date(ts).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Map a known notification kind (the underlying audit_log.action) onto a
 * Material Symbols ligature — a visual cue only. Unmapped kinds fall back to a
 * neutral bell glyph, so a server that emits a new kind still renders. Every
 * name here is in the @fse/ui icon registry (verify-icons guard).
 */
function kindIcon(kind: string): string {
  switch (kind) {
    case 'coop.member.invited':
      return 'group_add';
    case 'coop.member.removed':
      return 'group_remove';
    case 'coop.join.approved':
      return 'verified';
    case 'coop.join.rejected':
      return 'cancel';
    case 'kyc.reviewed':
      return 'badge';
    // Marketplace transactional kinds (audit_log actions emitted by the
    // marketplace / demand services).
    case 'offer.accepted':
      return 'check_circle';
    case 'order.delivered':
      return 'inventory_2';
    case 'demand.posted':
      return 'storefront';
    case 'demand.responded':
      return 'send';
    default:
      return 'notifications';
  }
}

/** Every in-app route a notification can deep-link to (tap-through target). */
type NotificationRoute =
  | '/cooperative'
  | '/verification'
  | '/home'
  | '/my-offers'
  | '/orders'
  | '/demands';

/**
 * Map a notification kind onto the in-app route that lets the user act on or
 * review it, so tapping a row goes somewhere useful. Mirrors the mobile
 * routeForKind (mobile route names differ). Returns null for an unknown kind →
 * the row only marks read on click.
 *
 * Marketplace kinds: an accepted offer lands the buyer on their offers list
 * (the row there now reads "Accepted"); a delivered order lands on the shared
 * /orders list; a posted/answered demand lands on the demand inbox (/demands),
 * the surface both a seller and a buyer reach it from.
 */
function routeForKind(kind: string): NotificationRoute | null {
  switch (kind) {
    case 'coop.member.invited':
    case 'coop.join.approved':
    case 'coop.join.rejected':
      return '/cooperative';
    case 'kyc.reviewed':
      return '/verification';
    case 'coop.member.removed':
      return '/home';
    case 'offer.accepted':
      return '/my-offers';
    case 'order.delivered':
      return '/orders';
    case 'demand.posted':
    case 'demand.responded':
      return '/demands';
    default:
      return null;
  }
}

/**
 * The row label. The server derives a `title` for every notification, but for
 * the marketplace kinds (offer / order / demand) we render a clear, fully
 * localised client string instead (the server label may be terse / English-only,
 * and these kinds postdate the original server copy). Every other kind keeps the
 * server-derived title verbatim, so a kind the client doesn't know about still
 * renders exactly what the server sent.
 */
function titleForRow(row: MyNotificationDto, t: TFunction): string {
  switch (row.kind) {
    case 'offer.accepted':
      return t('notifications.kind.offerAccepted');
    case 'order.delivered':
      return t('notifications.kind.orderDelivered');
    case 'demand.posted':
      return t('notifications.kind.demandPosted');
    case 'demand.responded':
      return t('notifications.kind.demandResponded');
    default:
      return row.title;
  }
}

/** Recency buckets a row falls into, in display order. */
type RecencyBucket = 'today' | 'yesterday' | 'earlier';

/**
 * Classify an ISO timestamp into Today / Yesterday / Earlier relative to `now`,
 * using LOCAL calendar-day boundaries (midnight), not a rolling 24/48h window —
 * so "yesterday at 23:00" reads as Yesterday even at 00:30 today. A row with an
 * unparseable date sinks to Earlier rather than throwing.
 */
function recencyBucket(iso: string, now: number): RecencyBucket {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return 'earlier';
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const todayMs = startOfToday.getTime();
  if (ts >= todayMs) return 'today';
  if (ts >= todayMs - 86_400_000) return 'yesterday';
  return 'earlier';
}

const BUCKET_ORDER: readonly RecencyBucket[] = ['today', 'yesterday', 'earlier'];
const BUCKET_LABEL_KEY: Record<RecencyBucket, string> = {
  today: 'notifications.group.today',
  yesterday: 'notifications.group.yesterday',
  earlier: 'notifications.group.earlier',
};

export function NotificationsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Accumulated rows across the pages we've loaded, plus the offset we want the
  // query to fetch next. `offset` doubles as the slice cursor.
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<MyNotificationDto[]>([]);
  const [total, setTotal] = useState<number | null>(null);

  // Per-device read state (no server read/unread — see notifications/readState).
  // localStorage-backed, so it's available synchronously on first render.
  const [readIds, setReadIds] = useState<Set<string>>(() => getReadIds());

  // Re-render once a minute so relative times don't freeze. Cheap — one
  // setState, no network or storage.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const query = useQuery<MyNotificationsPageDto, FseApiError>({
    queryKey: ['me-notifications', { limit: PAGE_SIZE, offset }],
    queryFn: ({ signal }) =>
      meControllerGetNotifications({ limit: PAGE_SIZE, offset }, signal),
  });

  const page = query.data;

  // Merge each fetched page into the accumulated list. Keyed by the page's own
  // offset so a re-render (or a refetch of the same offset) replaces rather
  // than duplicates that slice.
  useEffect(() => {
    if (!page) return;
    setTotal(page.pagination.total);
    setRows((prev) => {
      const next = prev.slice(0, page.pagination.offset);
      return [...next, ...page.data];
    });
  }, [page]);

  const hasMore = total !== null && rows.length < total;
  const isInitialLoad = query.isPending && rows.length === 0;
  // A page beyond the first is loading when the query is fetching for a
  // non-zero offset we haven't merged yet.
  const isLoadingMore = query.isFetching && offset > 0 && rows.length < (total ?? 0);
  const unreadCount = rows.reduce((n, r) => (readIds.has(r.id) ? n : n + 1), 0);

  // Click a row: optimistically mark it read on this device, persist, then
  // route to the screen where the user can act on it.
  function handleRowClick(row: MyNotificationDto) {
    setReadIds((prev) => new Set(prev).add(row.id));
    markRead(row.id);
    const to = routeForKind(row.kind);
    if (to) void navigate({ to });
  }

  // Mark every currently-loaded row read in one persisted write.
  function handleMarkAllRead() {
    setReadIds(markManyRead(rows.map((r) => r.id)));
  }

  // Bucket the (server-sorted, newest-first) rows by recency. The feed order is
  // preserved within each bucket, so Today/Yesterday/Earlier each read newest →
  // oldest. Recomputed each minute (nowTick) so a row crosses the midnight
  // boundary into the right bucket without a refetch.
  const grouped = BUCKET_ORDER.map((bucket) => ({
    bucket,
    items: rows.filter((r) => recencyBucket(r.occurredAt, nowTick) === bucket),
  })).filter((g) => g.items.length > 0);

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('notifications.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('notifications.subtitle')}
          </Text>
        </Box>
      </Box>

      {isInitialLoad ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : query.isError && rows.length === 0 ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              {t('notifications.retry')}
            </Button>
          }
        >
          {t('notifications.loadError', { error: query.error.message })}
        </Alert>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('notifications.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('notifications.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {unreadCount > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="text" size="small" onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </Button>
            </Box>
          ) : null}

          {grouped.map((group) => (
            <Box key={group.bucket}>
              <Box sx={{ mb: 1, px: 0.5 }}>
                <Text variant="label-large" color="text.secondary" as="h3">
                  {t(BUCKET_LABEL_KEY[group.bucket])}
                </Text>
              </Box>
              <Card>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {group.items.map((row, index) => (
                    <ListItem
                      key={row.id}
                      leadingIcon={kindIcon(row.kind)}
                      onClick={() => handleRowClick(row)}
                      title={
                        <Text variant="title-small" color="text.primary">
                          {titleForRow(row, t)}
                        </Text>
                      }
                      supportingText={
                        <Text variant="body-small" color="text.secondary">
                          {formatRelativeTime(row.occurredAt, nowTick, t)}
                        </Text>
                      }
                      trailing={
                        readIds.has(row.id) ? undefined : (
                          <Box
                            aria-label={t('notifications.unread')}
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                            }}
                          />
                        )
                      }
                      noDivider={index === group.items.length - 1}
                    />
                  ))}
                </Box>
              </Card>
            </Box>
          ))}

          {/* A page-load error after the first page has already rendered. */}
          {query.isError && rows.length > 0 ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => query.refetch()}>
                  {t('notifications.retry')}
                </Button>
              }
            >
              {t('notifications.loadMoreError', { error: query.error.message })}
            </Alert>
          ) : null}

          {hasMore ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setOffset(rows.length)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('notifications.loadingMore') : t('notifications.loadMore')}
              </Button>
            </Box>
          ) : (
            <Text variant="body-small" color="text.secondary">
              {total !== null && total > PAGE_SIZE
                ? t('notifications.showingAll', { total })
                : t('notifications.reachedEnd')}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default NotificationsScreen;
